/**
 * MonthlyClosePage - Havi Zaras Wizard (Monthly Close)
 *
 * Multi-step wizard for closing an accounting period.
 * Steps: Period Selection -> Summary -> Open Items -> VAT Summary -> Confirmation
 *
 * RBAC: Display = BOLTVEZETO+, Close action = ADMIN+, Reopen = SUPER_ADMIN only
 * API: GET /accounting/periods/:year/:month/summary
 *      POST /accounting/periods/:year/:month/close
 *      POST /accounting/periods/:year/:month/reopen
 *      GET /accounting/periods/:year/:month/vat-export?format=csv|xml
 */

import { api } from '@/api/client';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { cn, downloadAsFile, formatHuf, objectsToCsv } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Lock,
  LockOpen,
  XCircle,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

// ============================================
// Types
// ============================================

type PeriodStatus = 'OPEN' | 'CLOSED';

interface PeriodSummary {
  year: number;
  month: number;
  status: PeriodStatus;
  revenue: number;
  expenses: number;
  vatTotal: number;
  invoiceCount: number;
  transactionCount: number;
  openDeliveryNotes: number;
  unpaidInvoices: number;
  pendingSzhCount: number;
  closedAt?: string;
  closedBy?: string;
  closeNote?: string;
  vatFiledAt?: string;
  vatFiledBy?: string;
  vatFilingNote?: string;
}

interface VatBreakdownRow {
  vatRate: number;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  invoiceCount: number;
}

interface VatExportResponse {
  breakdown: VatBreakdownRow[];
  totals: {
    totalNet: number;
    totalVat: number;
    totalGross: number;
  };
  month: string;
}

interface PeriodHistoryItem {
  year: number;
  month: number;
  status: PeriodStatus;
  closedAt?: string;
  closedBy?: string;
  vatFiledAt?: string;
}

// ============================================
// Constants
// ============================================

const MONTHS_HU = [
  'januar',
  'februar',
  'marcius',
  'aprilis',
  'majus',
  'junius',
  'julius',
  'augusztus',
  'szeptember',
  'oktober',
  'november',
  'december',
] as const;

const WIZARD_STEPS = [
  'periodSelect',
  'summaryOverview',
  'openItems',
  'vatSummary',
  'confirmation',
] as const;

type WizardStep = (typeof WIZARD_STEPS)[number];

// ============================================
// Helper Functions
// ============================================

function getCurrentMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function formatMonthLabel(year: number, month: number): string {
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' });
}

// ============================================
// API Functions
// ============================================

async function fetchPeriodSummary(year: number, month: number): Promise<PeriodSummary> {
  return api.get<PeriodSummary>(
    `/accounting/periods/${year}/${String(month).padStart(2, '0')}/summary`
  );
}

async function fetchPeriodHistory(): Promise<PeriodHistoryItem[]> {
  return api.get<PeriodHistoryItem[]>('/accounting/periods/history');
}

async function fetchVatExport(year: number, month: number): Promise<VatExportResponse> {
  return api.get<VatExportResponse>(
    `/accounting/periods/${year}/${String(month).padStart(2, '0')}/vat-export?format=json`
  );
}

async function closePeriod(
  year: number,
  month: number,
  note: string
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(
    `/accounting/periods/${year}/${String(month).padStart(2, '0')}/close`,
    { note }
  );
}

async function reopenPeriod(
  year: number,
  month: number,
  reason: string
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(
    `/accounting/periods/${year}/${String(month).padStart(2, '0')}/reopen`,
    { reason }
  );
}

// ============================================
// Sub-components
// ============================================

/** Period Selector (year dropdown + month grid) */
function PeriodSelector({
  selectedYear,
  selectedMonth,
  onSelect,
}: {
  selectedYear: number;
  selectedMonth: number;
  onSelect: (year: number, month: number) => void;
}) {
  const { t } = useTranslation();
  const currentDate = getCurrentMonth();

  const years = useMemo(() => {
    const result: number[] = [];
    for (let y = currentDate.year; y >= currentDate.year - 3; y--) {
      result.push(y);
    }
    return result;
  }, [currentDate.year]);

  return (
    <div className="space-y-4">
      {/* Year selector */}
      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSelect(selectedYear - 1, selectedMonth)}
          disabled={selectedYear <= currentDate.year - 3}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <select
          value={selectedYear}
          onChange={e => onSelect(Number(e.target.value), selectedMonth)}
          className="rounded-md border border-border bg-background px-3 py-2 text-foreground"
        >
          {years.map(y => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSelect(selectedYear + 1, selectedMonth)}
          disabled={selectedYear >= currentDate.year}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
        {MONTHS_HU.map((monthKey, idx) => {
          const monthNum = idx + 1;
          const isFuture =
            selectedYear > currentDate.year ||
            (selectedYear === currentDate.year && monthNum > currentDate.month);
          const isSelected = monthNum === selectedMonth;

          return (
            <button
              key={monthKey}
              disabled={isFuture}
              onClick={() => onSelect(selectedYear, monthNum)}
              className={cn(
                'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                isSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : isFuture
                    ? 'cursor-not-allowed border-border bg-muted text-muted-foreground opacity-50'
                    : 'border-border bg-background text-foreground hover:border-primary hover:bg-primary/5'
              )}
            >
              {t(`finance.monthlyClose.months.${monthKey}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Period History Table */
function PeriodHistoryTable({ onSelect }: { onSelect: (year: number, month: number) => void }) {
  const { t } = useTranslation();
  const { data: history, isLoading } = useQuery({
    queryKey: ['accounting', 'period-history'],
    queryFn: fetchPeriodHistory,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        {t('finance.monthlyClose.noHistory')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">
              {t('finance.monthlyClose.periodColumn')}
            </th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">
              {t('finance.monthlyClose.statusColumn')}
            </th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">
              {t('finance.monthlyClose.closedAtColumn')}
            </th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">
              {t('finance.monthlyClose.closedByColumn')}
            </th>
            <th className="px-3 py-2 text-right font-medium text-muted-foreground">
              {t('common.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {history.map(item => (
            <tr
              key={`${item.year}-${item.month}`}
              className="border-b border-border last:border-0 hover:bg-muted/50"
            >
              <td className="px-3 py-2 font-medium text-foreground">
                {formatMonthLabel(item.year, item.month)}
              </td>
              <td className="px-3 py-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                    item.status === 'CLOSED'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                  )}
                >
                  {item.status === 'CLOSED' ? (
                    <Lock className="h-3 w-3" />
                  ) : (
                    <LockOpen className="h-3 w-3" />
                  )}
                  {t(
                    item.status === 'CLOSED'
                      ? 'finance.monthlyClose.statusClosed'
                      : 'finance.monthlyClose.statusOpen'
                  )}
                </span>
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {item.closedAt ? new Date(item.closedAt).toLocaleDateString('hu-HU') : '-'}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{item.closedBy ?? '-'}</td>
              <td className="px-3 py-2 text-right">
                <Button variant="outline" size="sm" onClick={() => onSelect(item.year, item.month)}>
                  {t('common.details')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function MonthlyClosePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore(s => s.user);
  const userRole = user?.role ?? '';

  // RBAC checks
  const canClose = ['ADMIN', 'SUPER_ADMIN', 'OWNER'].includes(userRole);
  const canReopen = userRole === 'SUPER_ADMIN';

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('periodSelect');
  const stepIndex = WIZARD_STEPS.indexOf(currentStep);

  // Period selection
  const current = getCurrentMonth();
  const [selectedYear, setSelectedYear] = useState(current.year);
  const [selectedMonth, setSelectedMonth] = useState(current.month);

  // Confirmation state
  const [confirmed, setConfirmed] = useState(false);
  const [closeNote, setCloseNote] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [closeSuccess, setCloseSuccess] = useState(false);

  // Data queries
  const monthStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ['accounting', 'period-summary', selectedYear, selectedMonth],
    queryFn: () => fetchPeriodSummary(selectedYear, selectedMonth),
    enabled: currentStep !== 'periodSelect',
  });

  const { data: vatExport, isLoading: vatLoading } = useQuery({
    queryKey: ['accounting', 'vat-export', selectedYear, selectedMonth],
    queryFn: () => fetchVatExport(selectedYear, selectedMonth),
    enabled: currentStep === 'vatSummary' || currentStep === 'confirmation',
  });

  // Mutations
  const closeMutation = useMutation({
    mutationFn: () => closePeriod(selectedYear, selectedMonth, closeNote),
    onSuccess: () => {
      setCloseSuccess(true);
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: () => reopenPeriod(selectedYear, selectedMonth, reopenReason),
    onSuccess: () => {
      setShowReopenDialog(false);
      setReopenReason('');
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
    },
  });

  // Navigation
  const handleNext = useCallback(() => {
    const nextIndex = stepIndex + 1;
    const nextStep = WIZARD_STEPS[nextIndex];
    if (nextStep) {
      setCurrentStep(nextStep);
    }
  }, [stepIndex]);

  const handleBack = useCallback(() => {
    const prevIndex = stepIndex - 1;
    const prevStep = WIZARD_STEPS[prevIndex];
    if (prevStep) {
      setCurrentStep(prevStep);
    }
  }, [stepIndex]);

  const handlePeriodSelect = useCallback((year: number, month: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);
    setConfirmed(false);
    setCloseNote('');
    setCloseSuccess(false);
  }, []);

  // Export handlers
  const handleCsvExport = useCallback(() => {
    if (!vatExport) return;

    const csvData = vatExport.breakdown.map(row => ({
      vatRate: `${row.vatRate}%`,
      netAmount: String(row.netAmount),
      vatAmount: String(row.vatAmount),
      grossAmount: String(row.grossAmount),
      invoiceCount: String(row.invoiceCount),
    }));

    const csv = objectsToCsv(csvData, [
      { key: 'vatRate', header: t('finance.monthlyClose.csvVatRate') },
      { key: 'netAmount', header: t('finance.monthlyClose.csvNet') },
      { key: 'vatAmount', header: t('finance.monthlyClose.csvVat') },
      { key: 'grossAmount', header: t('finance.monthlyClose.csvGross') },
      { key: 'invoiceCount', header: t('finance.monthlyClose.csvInvoiceCount') },
    ]);

    downloadAsFile(csv, `afa-export-${monthStr}.csv`);
  }, [vatExport, monthStr, t]);

  const handleXmlExport = useCallback(async () => {
    try {
      const blob = await api.downloadBlob(
        `/accounting/periods/${selectedYear}/${String(selectedMonth).padStart(2, '0')}/vat-export?format=xml`
      );
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = `afa-nav-m-${monthStr}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('XML export failed:', err);
    }
  }, [selectedYear, selectedMonth, monthStr]);

  // ============================================
  // Render helpers
  // ============================================

  const renderStepIndicator = () => (
    <div className="flex items-center gap-2 overflow-x-auto px-1 py-4">
      {WIZARD_STEPS.map((step, index) => (
        <div key={step} className="flex items-center gap-2">
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors',
              index < stepIndex
                ? 'bg-green-500 text-white'
                : index === stepIndex
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
            )}
          >
            {index < stepIndex ? <Check className="h-4 w-4" /> : index + 1}
          </div>
          {index < WIZARD_STEPS.length - 1 && (
            <div
              className={cn(
                'h-0.5 w-6 shrink-0 transition-colors',
                index < stepIndex ? 'bg-green-500' : 'bg-muted'
              )}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStepContent = () => {
    // Success screen (after close)
    if (closeSuccess) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-green-500" />
            <h2 className="mb-2 text-xl font-bold text-foreground">
              {t('finance.monthlyClose.closeSuccess')}
            </h2>
            <p className="mb-2 text-muted-foreground">
              {t('finance.monthlyClose.closeSuccessDesc', {
                period: formatMonthLabel(selectedYear, selectedMonth),
              })}
            </p>
            {summary && (
              <div className="mx-auto mt-4 max-w-sm space-y-2 text-left">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('finance.monthlyClose.revenue')}</span>
                  <span className="font-medium text-foreground">{formatHuf(summary.revenue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('finance.monthlyClose.expenses')}
                  </span>
                  <span className="font-medium text-foreground">{formatHuf(summary.expenses)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('finance.monthlyClose.vatTotal')}
                  </span>
                  <span className="font-medium text-foreground">{formatHuf(summary.vatTotal)}</span>
                </div>
              </div>
            )}
            <div className="mt-6">
              <Button
                onClick={() => {
                  setCloseSuccess(false);
                  setCurrentStep('periodSelect');
                }}
              >
                {t('common.back')}
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    switch (currentStep) {
      // ── Step 1: Period Selection ────────────────────
      case 'periodSelect':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {t('finance.monthlyClose.selectPeriod')}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t('finance.monthlyClose.selectPeriodDesc')}
                </p>
              </CardHeader>
              <CardContent>
                <PeriodSelector
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  onSelect={handlePeriodSelect}
                />
                <div className="mt-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t('finance.monthlyClose.selected')}:{' '}
                    <span className="font-semibold text-foreground">
                      {formatMonthLabel(selectedYear, selectedMonth)}
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('finance.monthlyClose.periodHistory')}</CardTitle>
              </CardHeader>
              <CardContent>
                <PeriodHistoryTable
                  onSelect={(year, month) => {
                    handlePeriodSelect(year, month);
                    handleNext();
                  }}
                />
              </CardContent>
            </Card>
          </div>
        );

      // ── Step 2: Summary Overview ───────────────────
      case 'summaryOverview':
        if (summaryLoading) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          );
        }
        if (summaryError || !summary) {
          return (
            <Card>
              <CardContent className="p-8 text-center">
                <XCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
                <p className="text-muted-foreground">{t('finance.monthlyClose.loadError')}</p>
              </CardContent>
            </Card>
          );
        }
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {t('finance.monthlyClose.summaryTitle', {
                    period: formatMonthLabel(selectedYear, selectedMonth),
                  })}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium',
                      summary.status === 'CLOSED'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                    )}
                  >
                    {summary.status === 'CLOSED' ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      <LockOpen className="h-3 w-3" />
                    )}
                    {t(
                      summary.status === 'CLOSED'
                        ? 'finance.monthlyClose.statusClosed'
                        : 'finance.monthlyClose.statusOpen'
                    )}
                  </span>
                  {/* ÁFA bevallás badge */}
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium',
                      summary.vatFiledAt
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-muted text-muted-foreground'
                    )}
                    title={
                      summary.vatFiledAt
                        ? t('finance.monthlyClose.vatFiledAt', {
                            date: new Date(summary.vatFiledAt).toLocaleDateString('hu-HU'),
                          })
                        : t('finance.monthlyClose.vatNotFiled')
                    }
                  >
                    <FileText className="h-3 w-3" />
                    {t(
                      summary.vatFiledAt
                        ? 'finance.monthlyClose.vatFiled'
                        : 'finance.monthlyClose.vatNotFiled'
                    )}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Revenue */}
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="text-sm text-muted-foreground">
                    {t('finance.monthlyClose.revenue')}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-green-600">
                    {formatHuf(summary.revenue)}
                  </p>
                </div>
                {/* Expenses */}
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="text-sm text-muted-foreground">
                    {t('finance.monthlyClose.expenses')}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-red-600">
                    {formatHuf(summary.expenses)}
                  </p>
                </div>
                {/* VAT */}
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="text-sm text-muted-foreground">
                    {t('finance.monthlyClose.vatTotal')}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-foreground">
                    {formatHuf(summary.vatTotal)}
                  </p>
                </div>
                {/* Invoice count */}
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="text-sm text-muted-foreground">
                    {t('finance.monthlyClose.invoiceCount')}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-foreground">{summary.invoiceCount}</p>
                </div>
                {/* Transaction count */}
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="text-sm text-muted-foreground">
                    {t('finance.monthlyClose.transactionCount')}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-foreground">
                    {summary.transactionCount}
                  </p>
                </div>
              </div>

              {/* Closed period info */}
              {summary.status === 'CLOSED' && summary.closedAt && (
                <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                  <p className="text-sm text-green-800 dark:text-green-300">
                    {t('finance.monthlyClose.closedInfo', {
                      date: new Date(summary.closedAt).toLocaleDateString('hu-HU'),
                      user: summary.closedBy ?? '-',
                    })}
                  </p>
                  {summary.closeNote && (
                    <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                      {t('finance.monthlyClose.noteLabel')}: {summary.closeNote}
                    </p>
                  )}

                  {/* Reopen button — SUPER_ADMIN only */}
                  {canReopen && (
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowReopenDialog(true)}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20"
                      >
                        <LockOpen className="mr-1 h-4 w-4" />
                        {t('finance.monthlyClose.reopenButton')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );

      // ── Step 3: Open Items ─────────────────────────
      case 'openItems':
        if (summaryLoading || !summary) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          );
        }
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('finance.monthlyClose.openItemsTitle')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('finance.monthlyClose.openItemsDesc')}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Open delivery notes */}
              <div
                className={cn(
                  'flex items-center justify-between rounded-lg border p-4',
                  summary.openDeliveryNotes > 0
                    ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                    : 'border-border bg-background'
                )}
              >
                <div className="flex items-center gap-2">
                  {summary.openDeliveryNotes > 0 ? (
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  <span className="font-medium text-foreground">
                    {t('finance.monthlyClose.openDeliveryNotes')}
                  </span>
                </div>
                <span
                  className={cn(
                    'text-xl font-bold',
                    summary.openDeliveryNotes > 0 ? 'text-amber-600' : 'text-green-600'
                  )}
                >
                  {summary.openDeliveryNotes}
                </span>
              </div>

              {/* Unpaid invoices */}
              <div
                className={cn(
                  'flex items-center justify-between rounded-lg border p-4',
                  summary.unpaidInvoices > 0
                    ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                    : 'border-border bg-background'
                )}
              >
                <div className="flex items-center gap-2">
                  {summary.unpaidInvoices > 0 ? (
                    <XCircle className="h-5 w-5 text-red-600" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  <span className="font-medium text-foreground">
                    {t('finance.monthlyClose.unpaidInvoices')}
                  </span>
                </div>
                <span
                  className={cn(
                    'text-xl font-bold',
                    summary.unpaidInvoices > 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {summary.unpaidInvoices}
                </span>
              </div>

              {/* Pending SZH */}
              <div
                className={cn(
                  'flex items-center justify-between rounded-lg border p-4',
                  summary.pendingSzhCount > 0
                    ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                    : 'border-border bg-background'
                )}
              >
                <div className="flex items-center gap-2">
                  {summary.pendingSzhCount > 0 ? (
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  <span className="font-medium text-foreground">
                    {t('finance.monthlyClose.pendingSzh')}
                  </span>
                </div>
                <span
                  className={cn(
                    'text-xl font-bold',
                    summary.pendingSzhCount > 0 ? 'text-amber-600' : 'text-green-600'
                  )}
                >
                  {summary.pendingSzhCount}
                </span>
              </div>

              {/* Warning if there are open items */}
              {(summary.openDeliveryNotes > 0 ||
                summary.unpaidInvoices > 0 ||
                summary.pendingSzhCount > 0) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    <AlertTriangle className="mr-1 inline h-4 w-4" />
                    {t('finance.monthlyClose.openItemsWarning')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        );

      // ── Step 4: VAT Summary ────────────────────────
      case 'vatSummary':
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('finance.monthlyClose.vatBreakdownTitle')}</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCsvExport}
                    disabled={!vatExport || vatLoading}
                  >
                    <Download className="mr-1 h-4 w-4" />
                    CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleXmlExport()}
                    disabled={vatLoading}
                  >
                    <FileText className="mr-1 h-4 w-4" />
                    XML (NAV)
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('finance.monthlyClose.vatBreakdownDesc')}
              </p>
            </CardHeader>
            <CardContent>
              {vatLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : vatExport && vatExport.breakdown.length > 0 ? (
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                            {t('finance.monthlyClose.vatRateColumn')}
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            {t('finance.monthlyClose.netColumn')}
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            {t('finance.monthlyClose.vatColumn')}
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            {t('finance.monthlyClose.grossColumn')}
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                            {t('finance.monthlyClose.invoiceCountShort')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {vatExport.breakdown.map(row => (
                          <tr key={row.vatRate} className="border-b border-border last:border-0">
                            <td className="px-3 py-2">
                              <span className="inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                {row.vatRate === 0 ? t('finance.vatExempt') : `${row.vatRate}%`}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-foreground">
                              {formatHuf(row.netAmount)}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-foreground">
                              {formatHuf(row.vatAmount)}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-foreground">
                              {formatHuf(row.grossAmount)}
                            </td>
                            <td className="px-3 py-2 text-right text-muted-foreground">
                              {row.invoiceCount}
                            </td>
                          </tr>
                        ))}
                        {/* Totals row */}
                        <tr className="border-t-2 border-border font-bold">
                          <td className="px-3 py-2 text-foreground">{t('finance.total')}</td>
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatHuf(vatExport.totals.totalNet)}
                          </td>
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatHuf(vatExport.totals.totalVat)}
                          </td>
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatHuf(vatExport.totals.totalGross)}
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">
                            {vatExport.breakdown.reduce((s, r) => s + r.invoiceCount, 0)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {t('finance.monthlyClose.noVatData')}
                </p>
              )}
            </CardContent>
          </Card>
        );

      // ── Step 5: Confirmation ───────────────────────
      case 'confirmation': {
        if (!summary) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          );
        }

        const isClosed = summary.status === 'CLOSED';

        return (
          <Card>
            <CardHeader>
              <CardTitle>
                {isClosed
                  ? t('finance.monthlyClose.periodAlreadyClosed')
                  : t('finance.monthlyClose.confirmTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isClosed ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                  <p className="text-sm text-green-800 dark:text-green-300">
                    {t('finance.monthlyClose.closedInfo', {
                      date: summary.closedAt
                        ? new Date(summary.closedAt).toLocaleDateString('hu-HU')
                        : '-',
                      user: summary.closedBy ?? '-',
                    })}
                  </p>
                  {canReopen && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400"
                      onClick={() => setShowReopenDialog(true)}
                    >
                      <LockOpen className="mr-1 h-4 w-4" />
                      {t('finance.monthlyClose.reopenButton')}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {/* Quick summary recap */}
                  <div className="space-y-2">
                    <div className="flex justify-between rounded-lg border border-border bg-background p-3">
                      <span className="text-muted-foreground">
                        {t('finance.monthlyClose.revenue')}
                      </span>
                      <span className="font-medium text-foreground">
                        {formatHuf(summary.revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between rounded-lg border border-border bg-background p-3">
                      <span className="text-muted-foreground">
                        {t('finance.monthlyClose.expenses')}
                      </span>
                      <span className="font-medium text-foreground">
                        {formatHuf(summary.expenses)}
                      </span>
                    </div>
                    <div className="flex justify-between rounded-lg border border-border bg-background p-3">
                      <span className="text-muted-foreground">
                        {t('finance.monthlyClose.vatTotal')}
                      </span>
                      <span className="font-medium text-foreground">
                        {formatHuf(summary.vatTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between rounded-lg border border-border bg-background p-3">
                      <span className="text-muted-foreground">
                        {t('finance.monthlyClose.invoiceCount')}
                      </span>
                      <span className="font-medium text-foreground">{summary.invoiceCount}</span>
                    </div>
                  </div>

                  {/* Note field */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      {t('finance.monthlyClose.closeNoteLabel')}
                    </label>
                    <textarea
                      rows={3}
                      value={closeNote}
                      onChange={e => setCloseNote(e.target.value)}
                      className="w-full rounded-lg border border-border bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder={t('finance.monthlyClose.closeNotePlaceholder')}
                    />
                  </div>

                  {/* Confirmation checkbox */}
                  <label className="flex items-start gap-3 rounded-lg border border-border bg-background p-4">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={e => setConfirmed(e.target.checked)}
                      className="mt-0.5 h-5 w-5 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">
                      {t('finance.monthlyClose.confirmCheckbox', {
                        period: formatMonthLabel(selectedYear, selectedMonth),
                      })}
                    </span>
                  </label>

                  {/* RBAC warning for non-admin */}
                  {!canClose && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                      <p className="text-sm text-amber-800 dark:text-amber-300">
                        <Lock className="mr-1 inline h-4 w-4" />
                        {t('finance.monthlyClose.noPermission')}
                      </p>
                    </div>
                  )}

                  {/* Error */}
                  {closeMutation.error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                      {closeMutation.error instanceof Error
                        ? closeMutation.error.message
                        : t('common.error')}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      }

      default:
        return null;
    }
  };

  // ============================================
  // Reopen Dialog
  // ============================================

  const renderReopenDialog = () => {
    if (!showReopenDialog) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        onKeyDown={e => {
          if (e.key === 'Escape') setShowReopenDialog(false);
        }}
      >
        <div className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-xl">
          <h3 className="mb-2 text-lg font-semibold text-foreground">
            {t('finance.monthlyClose.reopenTitle')}
          </h3>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('finance.monthlyClose.reopenDesc', {
              period: formatMonthLabel(selectedYear, selectedMonth),
            })}
          </p>
          <textarea
            rows={3}
            value={reopenReason}
            onChange={e => setReopenReason(e.target.value)}
            className="mb-4 w-full rounded-lg border border-border bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder={t('finance.monthlyClose.reopenReasonPlaceholder')}
          />
          {reopenMutation.error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {reopenMutation.error instanceof Error
                ? reopenMutation.error.message
                : t('common.error')}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowReopenDialog(false);
                setReopenReason('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => void reopenMutation.mutateAsync()}
              loading={reopenMutation.isPending}
              disabled={!reopenReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {t('finance.monthlyClose.reopenConfirm')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // Main Render
  // ============================================

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{t('finance.monthlyClose.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('finance.monthlyClose.subtitle')}</p>
      </div>

      {/* Step indicator */}
      {renderStepIndicator()}

      {/* Step title */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-foreground">
          {t(`finance.monthlyClose.steps.${currentStep}`)}
        </h2>
      </div>

      {/* Content */}
      {renderStepContent()}

      {/* Footer navigation */}
      {!closeSuccess && (
        <div className="mt-6 flex justify-between">
          <Button variant="outline" onClick={handleBack} disabled={stepIndex === 0}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            {t('common.back')}
          </Button>
          <div className="flex gap-3">
            {currentStep === 'confirmation' && summary?.status !== 'CLOSED' ? (
              <Button
                onClick={() => void closeMutation.mutateAsync()}
                loading={closeMutation.isPending}
                disabled={!confirmed || !canClose}
                className="bg-green-600 hover:bg-green-700"
              >
                <Lock className="mr-1 h-4 w-4" />
                {t('finance.monthlyClose.closeButton')}
              </Button>
            ) : stepIndex < WIZARD_STEPS.length - 1 ? (
              <Button onClick={handleNext}>
                {t('common.next')}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {/* Reopen Dialog */}
      {renderReopenDialog()}
    </div>
  );
}
